import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expensesApi } from '../api/expenses';
import type { Expense, ExpenseCategory, PaymentMode } from '@coreledger/shared-types';
import Pagination from '../components/Pagination';
import { confirm } from '../components/ConfirmDialog';
import { useRole } from '../context/AuthContext';

const fmt = (n: number) => '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
const today = () => new Date().toISOString().slice(0, 10);

const DIRECT_CATS: { value: string; label: string }[] = [
  { value: 'salary_wages', label: 'Salary & Wages' },
  { value: 'transport_fuel', label: 'Transport & Fuel' },
  { value: 'repairs_maintenance', label: 'Repairs & Maintenance' },
  { value: 'taxes_duties', label: 'Taxes & Duties' },
];
const INDIRECT_CATS: { value: string; label: string }[] = [
  { value: 'rent_lease', label: 'Rent / Lease' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'marketing', label: 'Marketing & Advertising' },
  { value: 'office_supplies', label: 'Office Supplies' },
  { value: 'professional_fees', label: 'Professional Fees' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'bank_charges', label: 'Bank Charges' },
  { value: 'miscellaneous', label: 'Miscellaneous' },
];
const GST_QUICK = [0, 5, 12, 18, 28];

const TDS_SECTIONS: { section: string; label: string; rate: number }[] = [
  { section: '192',            label: '192 — Salaries',                                         rate: 0    },
  { section: '193',            label: '193 — Interest on Securities',                            rate: 10   },
  { section: '194',            label: '194 — Dividends',                                         rate: 10   },
  { section: '393(1)(ca)',     label: '393(1)(ca) — Interest [prev. 194A]',                      rate: 10   },
  { section: '393(1)(f)',      label: '393(1)(f) — Contractors [prev. 194C]',                    rate: 2    },
  { section: '393(1)(g)',      label: '393(1)(g) — Insurance Commission [prev. 194D]',           rate: 5    },
  { section: '393(1)(j)',      label: '393(1)(j) — Commission on Lottery [prev. 194G]',          rate: 5    },
  { section: '393(1)(k)',      label: '393(1)(k) — Commission / Brokerage [prev. 194H]',         rate: 5    },
  { section: '393(1)(l)(i)',   label: '393(1)(l)(i) — Rent: Machinery/Equipment [prev. 194I]',  rate: 2    },
  { section: '393(1)(l)(ii)',  label: '393(1)(l)(ii) — Rent: Land / Building [prev. 194I]',     rate: 10   },
  { section: '393(1)(m)',      label: '393(1)(m) — Immovable Property Transfer [prev. 194IA]',  rate: 1    },
  { section: '393(1)(n)',      label: '393(1)(n) — Professional / Technical Services [prev. 194J]', rate: 10 },
  { section: '393(1)(o)',      label: '393(1)(o) — E-Commerce / Online Ads [prev. 194O]',       rate: 1    },
  { section: '393(1)(p)',      label: '393(1)(p) — Purchase of Goods [prev. 194Q]',             rate: 0.1  },
  { section: '393(1)(s)',      label: '393(1)(s) — Cash Withdrawal [prev. 194N]',               rate: 2    },
  { section: '195',            label: '195 — Payments to Non-Residents',                        rate: 20   },
];
const PAYMENT_MODES: PaymentMode[] = ['cash', 'upi', 'bank_transfer', 'cheque', 'other'];
const MODE_LABELS: Record<string, string> = { cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other' };
const BUILTIN_CAT_LABELS: Record<string, string> = [...DIRECT_CATS, ...INDIRECT_CATS].reduce((m, c) => ({ ...m, [c.value]: c.label }), {});

const CUSTOM_CATS_KEY = 'cl_custom_expense_cats';
type CustomCats = { direct: { value: string; label: string }[]; indirect: { value: string; label: string }[] };
function loadCustomCats(): CustomCats {
  try { return { direct: [], indirect: [], ...JSON.parse(localStorage.getItem(CUSTOM_CATS_KEY) ?? '{}') }; }
  catch { return { direct: [], indirect: [] }; }
}

function emptyExpense(): Partial<Expense> {
  return { date: today(), category: 'miscellaneous', group: 'indirect', amount: 0, paymentMode: 'bank_transfer', status: 'paid', gstRate: 0 };
}

function StatusBtn({ label, active, color, bg, onClick }: { label: string; active: boolean; color: string; bg: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '0.45rem 1.1rem', borderRadius: 8, border: `2px solid ${active ? color : '#e2e8f0'}`,
      background: active ? bg : 'white', color: active ? color : '#64748b', fontWeight: active ? 700 : 500,
      cursor: 'pointer', fontSize: '0.875rem',
    }}>{label}</button>
  );
}

export default function ExpensesPage() {
  const { canDelete } = useRole();
  const qc = useQueryClient();
  const [modal, setModal] = useState<null | 'create' | Expense>(null);
  const [form, setForm] = useState<Partial<Expense>>(emptyExpense());
  const [group, setGroup] = useState<'direct' | 'indirect'>('indirect');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const receiptInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [debouncedVendorFilter, setDebouncedVendorFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [customCats, setCustomCats] = useState<CustomCats>(loadCustomCats);
  const [manageCats, setManageCats] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Debounced text inputs — don't fire a request on every keystroke, same
  // pattern InvoicesPage/PartiesPage already use for their own search boxes.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedVendorFilter(vendorFilter); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [vendorFilter]);

  // Filters are applied server-side now (category/vendor/mode/search all
  // passed through to ExpensesController) rather than narrowed client-side
  // against just the current page's 50 rows — the old approach silently
  // missed any match sitting on a different page.
  const { data: expensesData, isLoading } = useQuery({
    queryKey: ['expenses', page, categoryFilter, debouncedVendorFilter, modeFilter, debouncedSearch],
    queryFn: () => expensesApi.list(page, 50, {
      category: categoryFilter || undefined,
      vendor: debouncedVendorFilter || undefined,
      mode: modeFilter || undefined,
      search: debouncedSearch || undefined,
    }),
    staleTime: 30 * 1000,
  });
  const expenses = expensesData?.data ?? [];
  const expensesMeta = expensesData?.meta;
  const del = useMutation({ mutationFn: expensesApi.delete, onSuccess: () => { qc.invalidateQueries({ queryKey: ['expenses'] }); qc.refetchQueries({ type: 'all' }); } });

  const filtersActive = categoryFilter !== '' || vendorFilter !== '' || modeFilter !== '' || search !== '';
  const totalPaid = expenses.filter(e => e.status === 'paid').reduce((s, e) => s + e.amount, 0);
  const totalUnpaid = expenses.filter(e => e.status !== 'paid').reduce((s, e) => s + e.amount, 0);

  function openCreate() {
    setForm(emptyExpense()); setGroup('indirect'); setError(null); setModal('create');
  }
  function openEdit(e: Expense) {
    setForm({ ...e }); setGroup((e.group ?? 'indirect') as 'direct' | 'indirect'); setError(null); setModal(e);
  }

  const baseCats = group === 'direct' ? DIRECT_CATS : INDIRECT_CATS;
  const cats = [...baseCats, ...(customCats[group] ?? [])];
  const catLabels: Record<string, string> = { ...BUILTIN_CAT_LABELS, ...customCats.direct.reduce((m, c) => ({ ...m, [c.value]: c.label }), {}), ...customCats.indirect.reduce((m, c) => ({ ...m, [c.value]: c.label }), {}) };
  // Both groups together (unlike `cats` above, which is scoped to whichever
  // group the create/edit form is currently on) — the Category filter isn't
  // tied to a group, so it needs every category a real expense could have.
  const allDirectCats = [...DIRECT_CATS, ...(customCats.direct ?? [])];
  const allIndirectCats = [...INDIRECT_CATS, ...(customCats.indirect ?? [])];

  function saveCustomCats(next: CustomCats) {
    setCustomCats(next);
    localStorage.setItem(CUSTOM_CATS_KEY, JSON.stringify(next));
  }

  function addCustomCat() {
    const label = newCatName.trim();
    if (!label) return;
    const slug = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const value = `custom_${slug}`;
    if (cats.some(c => c.value === value)) return;
    const next = { ...customCats, [group]: [...customCats[group], { value, label }] };
    saveCustomCats(next);
    setField('category', value as ExpenseCategory);
    setNewCatName('');
  }

  function removeCustomCat(g: 'direct' | 'indirect', value: string) {
    const next = { ...customCats, [g]: customCats[g].filter(c => c.value !== value) };
    saveCustomCats(next);
    if (form.category === value) setField('category', (g === 'direct' ? DIRECT_CATS[0].value : INDIRECT_CATS[0].value) as ExpenseCategory);
  }

  function setField<K extends keyof Expense>(k: K, v: Expense[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  async function handleSave() {
    if (!form.description?.trim()) { setError('Description is required'); return; }
    if (!form.amount || form.amount <= 0) { setError('Amount must be greater than zero'); return; }
    setError(null); setSaving(true);
    try {
      const payload = { ...form, group };
      if (modal === 'create') { await expensesApi.create(payload); }
      else { await expensesApi.update((modal as Expense).id, payload); }
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.refetchQueries({ type: 'all' });
      setModal(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  async function handleReceiptUpload(file: File) {
    if (modal === 'create' || !modal) { setError('Save the expense first before uploading a receipt'); return; }
    setUploadingReceipt(true); setError(null);
    try {
      const updated = await expensesApi.uploadReceipt((modal as Expense).id, file);
      setForm(f => ({ ...f, receiptPath: updated.receiptPath }));
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.refetchQueries({ type: 'all' });
    } catch (err) { setError(err instanceof Error ? err.message : 'Upload failed'); }
    finally { setUploadingReceipt(false); if (receiptInputRef.current) receiptInputRef.current.value = ''; }
  }

  const gstAmt = (form.amount ?? 0) * ((form.gstRate ?? 0) / 100);
  const totalPayable = (form.amount ?? 0) + gstAmt;

  const STATUS_CLS: Record<string, string> = { paid: 'badge-paid', unpaid: 'badge-unpaid', partial: 'badge-blue' };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Expenses</h1>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--danger)', fontWeight: 600 }}>{fmt(totalUnpaid)} unpaid</span>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>|</span>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-2)', fontWeight: 600 }}>{fmt(totalPaid + totalUnpaid)} total</span>
          </div>
          <button className="btn btn-primary" onClick={openCreate}>+ New Expense</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by description or vendor…" style={{ maxWidth: 300 }} />
        <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }} style={{ width: 'auto' }}>
          <option value="">All categories</option>
          <optgroup label="Direct Expenses">
            {allDirectCats.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </optgroup>
          <optgroup label="Indirect Expenses">
            {allIndirectCats.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </optgroup>
        </select>
        <input value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} placeholder="Filter by vendor…" style={{ maxWidth: 220 }} />
        <select value={modeFilter} onChange={e => { setModeFilter(e.target.value); setPage(1); }} style={{ width: 'auto' }}>
          <option value="">All modes</option>
          {PAYMENT_MODES.map(m => <option key={m} value={m}>{MODE_LABELS[m]}</option>)}
        </select>
        {filtersActive && (
          <button
            type="button"
            onClick={() => { setSearch(''); setCategoryFilter(''); setVendorFilter(''); setModeFilter(''); setPage(1); }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-muted)', textDecoration: 'underline', fontSize: '0.82rem' }}
          >Clear filters</button>
        )}
      </div>

      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : expenses.length === 0 ? <div className="empty-state"><div className="empty-state-icon">💸</div><div className="empty-state-text">{filtersActive ? 'No expenses match your filters' : 'No expenses yet'}</div></div>
          : (
            <table>
              <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Vendor</th><th>Mode</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>GST</th><th>Status</th><th>Receipt</th><th style={{ width: 100 }}>Actions</th></tr></thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e.id}>
                    <td>{e.date}</td>
                    <td><span className="badge badge-gray">{catLabels[e.category] ?? e.category}</span></td>
                    <td style={{ fontWeight: 500 }}>{e.description}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{e.vendor ?? '—'}</td>
                    <td style={{ fontSize: '0.83rem' }}>{MODE_LABELS[e.paymentMode] ?? e.paymentMode}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--danger)' }}>{fmt(e.amount)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: '0.85rem' }}>{e.gstRate ? `${e.gstRate}%` : '—'}</td>
                    <td><span className={`badge ${STATUS_CLS[e.status ?? 'paid'] ?? 'badge-gray'}`}>{e.status ?? 'paid'}</span></td>
                    <td style={{ fontSize: '0.82rem' }}>
                      {e.receiptPath
                        ? e.receiptType === 'file'
                          ? <button className="btn btn-ghost btn-sm" style={{ padding: '0.1rem 0.5rem', fontSize: '0.78rem' }} onClick={() => setPreviewUrl(expensesApi.receiptDownloadUrl(e.id))}>📎 View</button>
                          : <a href={e.receiptPath} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>🔗 View</a>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(e)}>Edit</button>
                        {canDelete && <button className="btn btn-danger btn-sm" disabled={del.isPending} onClick={async () => { if (await confirm({ message: 'Delete expense?', danger: true })) del.mutate(e.id); }}>Del</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {expensesMeta && expensesMeta.totalPages > 1 && (
          <Pagination page={page} totalPages={expensesMeta.totalPages} onChange={setPage} />
        )}
      </div>

      {/* Create / Edit modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 680 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'New Expense' : 'Edit Expense'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box">{error}</div>}

              {/* Group toggle */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>Expense Type</div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {(['direct', 'indirect'] as const).map(g => (
                    <button key={g} type="button" onClick={() => {
                      setGroup(g);
                      const firstCat = g === 'direct' ? DIRECT_CATS[0] : INDIRECT_CATS[0];
                      if (firstCat) setField('category', firstCat.value as ExpenseCategory);
                    }} style={{
                      flex: 1, padding: '0.6rem 1rem', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${group === g ? (g === 'direct' ? '#d97706' : 'var(--primary)') : '#e2e8f0'}`,
                      background: group === g ? (g === 'direct' ? '#fef3c7' : '#eff6ff') : 'white',
                      color: group === g ? (g === 'direct' ? '#92400e' : 'var(--primary)') : '#64748b',
                      fontWeight: group === g ? 700 : 500, fontSize: '0.875rem',
                    }}>
                      {g === 'direct' ? 'Direct Expense' : 'Indirect Expense'}
                      <div style={{ fontSize: '0.72rem', fontWeight: 400, marginTop: '0.15rem', opacity: 0.8 }}>
                        {g === 'direct' ? 'Wages, transport, repairs' : 'Rent, utilities, admin'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Category */}
              <div className="form-row form-row-2">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Category *</span>
                    <button type="button" onClick={() => setManageCats(m => !m)} style={{ fontSize: '0.75rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      {manageCats ? '✕ Close' : '⚙ Manage'}
                    </button>
                  </div>
                  <select value={form.category ?? ''} onChange={e => setField('category', e.target.value as ExpenseCategory)}>
                    {cats.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  {manageCats && (
                    <div style={{ marginTop: '0.5rem', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.65rem' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Custom {group} categories
                      </div>
                      {customCats[group].length === 0 && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>None added yet.</div>
                      )}
                      {customCats[group].map(c => (
                        <div key={c.value} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.2rem 0' }}>
                          <span style={{ fontSize: '0.83rem' }}>{c.label}</span>
                          <button type="button" onClick={() => removeCustomCat(group, c.value)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '0.85rem', padding: '0 0.25rem' }}>✕</button>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}>
                        <input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="New category name" style={{ flex: 1, padding: '0.3rem 0.5rem', fontSize: '0.83rem' }}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomCat(); } }} />
                        <button type="button" className="btn btn-primary btn-sm" onClick={addCustomCat}>Add</button>
                      </div>
                    </div>
                  )}
                </div>
                <label>Vendor / Payee
                  <input value={form.vendor ?? ''} onChange={e => setField('vendor', e.target.value)} placeholder="Vendor name" />
                </label>
              </div>

              {/* Description */}
              <div className="form-row">
                <label>Description *
                  <input value={form.description ?? ''} onChange={e => setField('description', e.target.value)} placeholder="What is this expense for?" autoFocus={modal === 'create'} />
                </label>
              </div>

              {/* Status buttons */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>Payment Status</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <StatusBtn label="Paid" active={form.status === 'paid'} color="#16a34a" bg="#f0fdf4" onClick={() => setField('status', 'paid')} />
                  <StatusBtn label="Partial" active={form.status === 'partial'} color="#d97706" bg="#fefce8" onClick={() => setField('status', 'partial')} />
                  <StatusBtn label="Unpaid" active={form.status === 'unpaid'} color="var(--danger)" bg="#fef2f2" onClick={() => setField('status', 'unpaid')} />
                  {(form.status === 'partial' || form.status === 'unpaid') && (
                    <div style={{ display: 'flex', gap: '0.75rem', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.5rem 0.75rem' }}>
                      {form.status === 'partial' && (
                        <label style={{ fontWeight: 500, fontSize: '0.82rem', margin: 0 }}>Paid so far (₹)
                          <input type="number" min={0} step={0.01} value={form.partialPaidAmount ?? ''} onChange={e => setField('partialPaidAmount', parseFloat(e.target.value) || 0)} style={{ width: 100, marginTop: '0.2rem', display: 'block' }} />
                        </label>
                      )}
                      <label style={{ fontWeight: 500, fontSize: '0.82rem', margin: 0 }}>Due Date
                        <input type="date" value={form.dueDate ?? ''} onChange={e => setField('dueDate', e.target.value)} style={{ marginTop: '0.2rem', display: 'block' }} />
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Amount + Date + Mode */}
              <div className="form-row form-row-3">
                <label>Date *<input type="date" value={form.date ?? ''} onChange={e => setField('date', e.target.value)} /></label>
                <label>Amount (₹) *<input type="number" min={0} step={0.01} value={form.amount || ''} onChange={e => setField('amount', parseFloat(e.target.value) || 0)} placeholder="0.00" /></label>
                <label>Payment Mode
                  <select value={form.paymentMode ?? 'bank_transfer'} onChange={e => setField('paymentMode', e.target.value as PaymentMode)}>
                    {PAYMENT_MODES.map(m => <option key={m} value={m}>{MODE_LABELS[m]}</option>)}
                  </select>
                </label>
              </div>

              {/* GST section */}
              <div style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: (form.gstRate ?? 0) > 0 ? '0.75rem' : 0, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-2)', marginRight: '0.25rem' }}>GST on this expense?</span>
                  {GST_QUICK.map(r => (
                    <button key={r} type="button" onClick={() => { setField('gstRate', r); if (r === 0) setField('gstTaxType', undefined); else if (!form.gstTaxType) setField('gstTaxType', 'cgst_sgst'); }}
                      style={{ padding: '0.25rem 0.65rem', borderRadius: 6, border: `2px solid ${(form.gstRate ?? 0) === r ? 'var(--primary)' : '#e2e8f0'}`, background: (form.gstRate ?? 0) === r ? '#eff6ff' : 'white', color: (form.gstRate ?? 0) === r ? 'var(--primary)' : '#64748b', fontWeight: (form.gstRate ?? 0) === r ? 700 : 500, fontSize: '0.82rem', cursor: 'pointer' }}>
                      {r === 0 ? 'None' : `${r}%`}
                    </button>
                  ))}
                </div>
                {(form.gstRate ?? 0) > 0 && (
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {(['cgst_sgst', 'igst'] as const).map(tt => (
                        <button key={tt} type="button" onClick={() => setField('gstTaxType', tt)}
                          style={{ padding: '0.25rem 0.75rem', borderRadius: 6, border: `2px solid ${form.gstTaxType === tt ? 'var(--primary)' : '#e2e8f0'}`, background: form.gstTaxType === tt ? '#eff6ff' : 'white', color: form.gstTaxType === tt ? 'var(--primary)' : '#64748b', fontWeight: form.gstTaxType === tt ? 700 : 500, fontSize: '0.82rem', cursor: 'pointer' }}>
                          {tt === 'cgst_sgst' ? 'CGST + SGST' : 'IGST'}
                        </button>
                      ))}
                    </div>
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                      GST: ₹{gstAmt.toFixed(2)} &nbsp;|&nbsp; Total payable: ₹{totalPayable.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* TDS Payable */}
              <div style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>TDS Payable (Income Tax Act 2025)</div>
                <div className="form-row form-row-2">
                  <label style={{ fontSize: '0.82rem', fontWeight: 500 }}>TDS Section
                    <select value={form.tdsSection ?? ''} onChange={e => {
                      const sec = TDS_SECTIONS.find(s => s.section === e.target.value);
                      setField('tdsSection', e.target.value || undefined as unknown as string);
                      if (sec) setField('tdsRate', sec.rate);
                    }} style={{ marginTop: '0.25rem' }}>
                      <option value="">— No TDS —</option>
                      {TDS_SECTIONS.map(s => <option key={s.section} value={s.section}>{s.label} ({s.rate}%)</option>)}
                    </select>
                  </label>
                  <label style={{ fontSize: '0.82rem', fontWeight: 500 }}>TDS Rate (%)
                    <input type="number" min={0} max={100} step={0.01} value={form.tdsRate ?? ''} onChange={e => setField('tdsRate', parseFloat(e.target.value) || 0)} placeholder="Auto-filled" style={{ marginTop: '0.25rem' }} />
                  </label>
                </div>
                {form.tdsSection && (form.tdsRate ?? 0) > 0 && (
                  <div className="success-box" style={{ padding: '0.5rem 0.75rem', fontSize: '0.82rem', marginTop: '0.5rem', marginBottom: 0 }}>
                    TDS amount: <strong>₹{((form.amount ?? 0) * (form.tdsRate ?? 0) / 100).toFixed(2)}</strong>
                    &nbsp;| Net payable: <strong>₹{((form.amount ?? 0) - (form.amount ?? 0) * (form.tdsRate ?? 0) / 100).toFixed(2)}</strong>
                  </div>
                )}
              </div>

              {/* Notes */}
              <label style={{ fontWeight: 500, fontSize: '0.875rem', display: 'block', marginBottom: '0.75rem' }}>Notes
                <textarea value={form.notes ?? ''} onChange={e => setField('notes', e.target.value)} rows={2} placeholder="Additional details…" style={{ marginTop: '0.35rem', width: '100%', resize: 'vertical' }} />
              </label>

              {/* Receipt — file upload or URL */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem 1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>Receipt / Bill</div>

                {/* Existing receipt */}
                {form.receiptPath && (
                  <div className="success-box" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0.75rem' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>📎 Receipt attached</span>
                    {form.receiptType === 'file' ? (
                      <a href={modal !== 'create' && modal ? expensesApi.receiptDownloadUrl((modal as Expense).id) : '#'} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', color: 'var(--primary)', marginLeft: 'auto' }}>⬇ Download</a>
                    ) : (
                      <a href={form.receiptPath} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', color: 'var(--primary)', marginLeft: 'auto' }}>🔗 View →</a>
                    )}
                    <button type="button" onClick={() => setField('receiptPath', '')} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '0.85rem', padding: '0 0.25rem' }}>✕</button>
                  </div>
                )}

                {/* Upload file */}
                {modal !== 'create' && modal && (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <input ref={receiptInputRef} type="file" accept="image/*,.pdf,.jpg,.jpeg,.png" style={{ flex: 1, fontSize: '0.82rem' }}
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleReceiptUpload(f); }} disabled={uploadingReceipt} />
                    {uploadingReceipt && <span className="spinner" style={{ width: 16, height: 16 }} />}
                  </div>
                )}
                {modal === 'create' && (
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>Save the expense first, then upload a receipt file.</p>
                )}

                {/* Or paste URL */}
                {form.receiptType !== 'file' && (
                  <label style={{ fontWeight: 500, fontSize: '0.82rem', display: 'block' }}>Or paste link
                    <input value={form.receiptPath ?? ''} onChange={e => setField('receiptPath', e.target.value)} placeholder="Google Drive, Dropbox, or any URL…" style={{ marginTop: '0.25rem', fontSize: '0.82rem' }} />
                  </label>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save Expense'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt preview modal */}
      {previewUrl && (
        <div className="modal-overlay" onClick={() => setPreviewUrl(null)} style={{ zIndex: 1100 }}>
          <div className="modal" style={{ maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Receipt Preview</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <a href={previewUrl} download className="btn btn-ghost btn-sm">⬇ Download</a>
                <button className="modal-close" onClick={() => setPreviewUrl(null)}>×</button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem', background: 'var(--surface-alt)', minHeight: 400 }}>
              {/\.(pdf)$/i.test(previewUrl) || previewUrl.includes('/receipt') ? (
                <iframe src={previewUrl} title="Receipt" style={{ width: '100%', height: 520, border: 'none', borderRadius: 4 }} />
              ) : (
                <img src={previewUrl} alt="Receipt" style={{ maxWidth: '100%', maxHeight: 520, display: 'block', margin: '0 auto', borderRadius: 4 }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
